/** Contrato entre o front e o servidor. O mock e o cliente HTTP implementam o mesmo. */

import type { CombatIdleConfig } from '../data/combateIdle';
import type { TabelaCombate } from '../data/combateTabela';
import type { AltarTroca, Chefe, EventoAbrirBody, EventoAgenda, EventoAtual } from './evento';

export type { CombatIdleAntidoto, CombatIdleConfig, CombatIdleCura } from '../data/combateIdle';
export type { AltarOferta, AltarTroca, Chefe, ChefeAoVivo, ChefeEstado, ChefeVariante } from './evento';
export type { EventoAbrirBody, EventoAgenda, EventoAtual, EventoDropItem, EventoEstado, EventoKitPeca, EventoPremio, EventoProximo, EventoRaridade, EventoTabela } from './evento';

export interface Account {
  readonly id: string;
  readonly displayName: string;
  readonly role?: 'player' | 'gm' | 'admin';
  /** pt-BR, en-US ou es-ES. Ausente enquanto a conta nunca escolheu. */
  readonly idioma?: string | null;
}

export type ShardStatus = 'aberto' | 'cheio' | 'manutencao';

export interface Shard {
  readonly id: string;
  readonly name: string;
  readonly region: string;
  readonly online: number;
  readonly capacity: number;
  readonly status: ShardStatus;
  readonly ping: number;
}

export type Notoriety = 'inocente' | 'criminoso' | 'assassino' | 'duelista';
export type Sex = 'male' | 'female';
export type ProfessionId = 'assassin' | 'archer' | 'warrior' | 'mage' | 'swordsman' | 'smith' | 'advanced';

export interface CharacterSummary {
  readonly id: string;
  readonly shardId: string;
  readonly name: string;
  readonly profession: string;
  readonly skillTotal: number;
  readonly homeCity: string;
  readonly notoriety: Notoriety;
  readonly lastSeen: string;
  /** Pasta em /sprites/character/{skin}/. */
  readonly skin: string;
  /** Hue UO da pele (1002–1058). Só pinta skins cinza como human-male. */
  readonly skinHue: number;
  /** activity = Dead. Na praça vira ghost e não monta. */
  readonly dead: boolean;
  /** Dicas da primeira entrada. Só o personagem recém-criado nasce com isso ligado. */
  readonly tutorial?: boolean;
}

export interface Appearance {
  readonly skinHue: number;
  readonly hairStyle: string;
  readonly hairHue: number;
  readonly beardStyle?: string | null;
  readonly beardHue?: number | null;
  readonly shirtHue: number;
  readonly pantsHue: number;
}

export interface CharacterStats {
  readonly str: number;
  readonly dex: number;
  readonly int: number;
}

export interface CharacterSkillPick {
  readonly id: string;
  readonly value: number;
  readonly label?: string;
}

export interface CharacterDraft {
  name: string;
  sex: Sex;
  profession: ProfessionId;
  stats: CharacterStats;
  skills: CharacterSkillPick[];
  appearance: Appearance;
  homeCity: string;
}

export interface CharacterDetail extends CharacterSummary {
  readonly sex: Sex;
  readonly stats: CharacterStats;
  readonly skills: readonly CharacterSkillPick[];
  readonly appearance: Appearance;
}

export interface CatalogItem {
  readonly id: string;
  readonly label: string;
}

export interface CatalogProfession extends CatalogItem {
  readonly stats: CharacterStats | null;
  readonly skills: readonly CharacterSkillPick[] | null;
}

export interface CatalogCity extends CatalogItem {
  readonly blurb: string;
}

export interface CatalogSkill extends CatalogItem {
  readonly group: string;
}

export interface HueRange {
  readonly min: number;
  readonly max: number;
}

export interface CreationCatalog {
  readonly professions: readonly CatalogProfession[];
  readonly skills: readonly CatalogSkill[];
  readonly cities: readonly CatalogCity[];
  readonly maleHair: readonly CatalogItem[];
  readonly femaleHair: readonly CatalogItem[];
  readonly beards: readonly CatalogItem[];
  readonly skinHue: HueRange;
  readonly clothHue: HueRange;
}

export interface CosmeticPatch {
  readonly hairStyle: string;
  readonly hairHue: number;
  readonly beardStyle?: string | null;
  readonly beardHue?: number | null;
  readonly shirtHue: number;
  readonly pantsHue: number;
}

export interface Credentials {
  readonly email: string;
  readonly password: string;
  readonly serial?: string;
}

/** O código do e-mail (techspec-confirmacao-email): digitado ou vindo do link. */
export interface CodigoEmail {
  readonly email: string;
  readonly codigo: string;
  readonly serial?: string;
}

/** "Esqueci a senha" (techspec-esqueci-senha): o código do e-mail e a senha nova. */
export interface RedefinirSenha {
  readonly email: string;
  readonly codigo: string;
  readonly senha: string;
  readonly serial?: string;
}

/** O código de quem respondeu 202/403 com este código ainda não confirmou o e-mail. */
export const CONFIRMAR_EMAIL = 'auth.confirmarEmail';
/** Senha certa de uma conta da equipe: falta o código do app autenticador (techspec-console-2fa-e-cloudflare-access §03.4). */
export const DOIS_FATORES = 'auth.doisFatores';
export const DOIS_FATORES_DESAFIO = 'auth.doisFatoresDesafio';
export interface CodigoDoisFatores { desafio: string; codigo?: string; recuperacao?: string }

/** Entrar com o Google: o ID token do botão do Google e o convite, quando houver. */
export interface CredencialGoogle {
  readonly credencial: string;
  readonly serial?: string;
}

export interface FasePublica {
  readonly nome: string;
  readonly tipo: string;
  readonly comecaEm: string;
}

export interface AcessoEstado {
  readonly inscricoesAbertas: boolean;
  readonly restrito: boolean;
  /** A fase ativa. */
  readonly fase: FasePublica | null;
  /** Só sem fase ativa: a próxima que está mandando convites (techspec-landing-fase §04.1). */
  readonly proxima: FasePublica | null;
  /** Chave pública do Turnstile; null quando o servidor não usa anti-robô. */
  readonly antiRobo: { readonly chaveSite: string } | null;
  /** Configurações › Redes no console. Ausente num servidor antigo: a landing usa os links padrão. */
  readonly redes?: RedesPublicas | null;
}

/** O Instagram e o Discord do jogo, como estão no console (techspec-marca §08). */
export interface RedesPublicas {
  readonly instagram: string;
  readonly instagramUsuario: string;
  readonly discord: string;
}

export interface InscricaoAcesso {
  readonly email: string;
  readonly idioma: string;
  readonly origem: string | null;
  readonly aceite: boolean;
  readonly site?: string;
  /** Token do Turnstile, quando o estado trouxe `antiRobo`. */
  readonly antiRobo?: string;
}

export interface LojaOuro {
  readonly vendaAberta: boolean;
  readonly motivo: string | null;
  readonly codigo: string | null;
  readonly ambiente: 'teste' | 'producao';
  /** Só no ambiente local: o pagamento é simulado e sempre aprova (techspec-pagamentos-simulados). */
  readonly simulado?: boolean;
  readonly metodos: readonly string[];
  readonly pacotes: readonly { readonly id: string; readonly ouro: number; readonly centavos: number }[];
  readonly semana: { readonly limiteOuro: number; readonly usadoOuro: number };
  readonly pixExpiraMin: number;
  readonly pixConsultaS: number;
  /** Cartão (Checkout Pro, techspec-checkout-pro): o teto de parcelas que a página do Mercado Pago oferece. */
  readonly parcelasMax?: number;
  readonly politica: { readonly versao: string; readonly texto: string };
  readonly aviso18: string;
  readonly bloqueio: { readonly em: string; readonly motivo: string | null } | null;
  readonly personagem: { readonly id: string; readonly nome: string } | null;
  readonly pendentes: readonly { readonly id: string; readonly metodo: string; readonly ouro: number; readonly centavos: number; readonly expiraEm: string | null }[];
}

export interface PedidoOuroBody {
  readonly pacoteId: string;
  readonly characterId: string | null;
  readonly metodo: 'pix' | 'cartao';
  readonly chave: string;
  readonly politicaVersao: string;
  readonly aceite: boolean;
}

/** Uma compra de ouro com dinheiro no histórico. Sem dado de pagamento além do método. */
export interface HistoricoOuro {
  readonly id: string;
  readonly estado: string;
  readonly metodo: string;
  readonly ouro: number;
  readonly centavos: number;
  readonly criadoEm: string;
  readonly pagoEm: string | null;
  readonly personagem: string | null;
}

/** Um pedido de ouro. O QR e o "copia e cola" só vêm enquanto aguarda. */
export interface PagamentoOuro {
  readonly id: string;
  readonly estado: string;
  readonly metodo: string;
  readonly pacoteId: string;
  readonly ouro: number;
  readonly centavos: number;
  readonly pixCopiaCola: string | null;
  readonly pixQrBase64: string | null;
  readonly criadoEm: string;
  readonly expiraEm: string | null;
  readonly pagoEm: string | null;
  readonly personagem: string | null;
  readonly ambiente: 'teste' | 'producao';
  /** O motivo da recusa do cartão (status_detail do Mercado Pago). */
  readonly detalhe?: string | null;
  /** O cartão enquanto aguarda: a página do Checkout Pro (só HTTPS do Mercado Pago, conferida no servidor). */
  readonly checkout?: string | null;
}

export class ApiError extends Error {
  constructor(message: string, readonly status = 0, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Não é erro: é o próximo passo da entrada de uma conta da equipe (o código do app). */
export class DesafioDoisFatores extends ApiError {
  constructor(readonly desafio: string) {
    super('', 200, DOIS_FATORES);
    this.name = 'DesafioDoisFatores';
  }
}

export interface Friend {
  readonly id: string;
  readonly name: string;
  readonly online: boolean;
}

export interface FriendLookup {
  readonly name: string;
  readonly online: boolean;
}

/** Os três Founder Packs (techspec-founders-pack). O conteúdo de cada um fica no site e no servidor, fixo. */
export type FounderTier = 'peregrino' | 'cavaleiro' | 'lorde';

/** A vitrine pública dos packs: a chave de vendas, o preço e o "ativo" do console. */
export interface FounderEstado {
  readonly vendasAbertas: boolean;
  /** Vendas abertas, gateway no ar e ao menos um método ligado. */
  readonly pagamentoDisponivel: boolean;
  readonly packs: readonly {
    readonly id: FounderTier;
    readonly centavos: number;
    readonly ativo: boolean;
    readonly vipDias: number;
    readonly ouro: number;
  }[];
  readonly metodos: readonly ('pix' | 'cartao')[];
  readonly ambiente: 'teste' | 'producao';
  /** Só no ambiente local: o pagamento é simulado e sempre aprova (techspec-pagamentos-simulados). */
  readonly simulado?: boolean;
  readonly parcelasMax: number;
  readonly pixExpiraMin: number;
  readonly politica: { readonly versao: string; readonly texto: string };
  readonly aviso18: string;
}

/** O código do e-mail conferido: o token curto libera o pedido; o pack atual só vem depois do código. */
export interface FounderConferido {
  readonly token: string;
  readonly tier: FounderTier | null;
  readonly pagamentoAberto: boolean;
}

export interface FounderPedidoBody {
  readonly email: string;
  readonly token: string;
  readonly tier: FounderTier;
  readonly metodo: 'pix' | 'cartao';
  /** Idempotência: o mesmo clique repetido devolve o mesmo pedido. */
  readonly chave: string;
  readonly politicaVersao: string;
  readonly aceite: boolean;
  readonly maior18: boolean;
  readonly preRegistro: boolean;
  readonly idioma: string;
}

/** Um pedido de Founder Pack. O segredo só vem na criação: o site o usa para consultar o pedido. */
export interface FounderPedido {
  readonly id: string;
  readonly estado: string;
  readonly metodo: 'pix' | 'cartao';
  readonly tier: FounderTier;
  readonly tierAnterior: FounderTier | null;
  readonly centavos: number;
  readonly pixCopiaCola: string | null;
  readonly pixQrBase64: string | null;
  readonly criadoEm: string;
  readonly expiraEm: string | null;
  readonly pagoEm: string | null;
  readonly ambiente: 'teste' | 'producao';
  readonly detalhe?: string | null;
  /** O cartão enquanto aguarda: a página do Checkout Pro. */
  readonly checkout?: string | null;
  readonly segredo?: string | null;
}

export interface GameApi {
  signIn(credentials: Credentials): Promise<Account>;
  signUp(credentials: Credentials): Promise<Account>;
  signInGoogle(body: CredencialGoogle): Promise<Account>;
  /** O código do app autenticador depois da senha certa de uma conta da equipe. */
  signIn2fa(body: CodigoDoisFatores): Promise<Account>;
  /** Confere o código do e-mail e, certo, abre a sessão. */
  confirmarEmail(body: CodigoEmail): Promise<Account>;
  /** Pede outro código. Sempre resolve igual: não diz se o e-mail existe. */
  reenviarCodigo(email: string): Promise<void>;
  /** "Esqueci a senha": manda o código. Sempre resolve igual: não diz se o e-mail tem conta. */
  esqueciSenha(email: string): Promise<void>;
  /** Troca a senha com o código do e-mail e, certo, abre a sessão (as outras caem). */
  redefinirSenha(body: RedefinirSenha): Promise<Account>;
  estadoAcesso(): Promise<AcessoEstado>;
  inscreverAcesso(body: InscricaoAcesso): Promise<{ message: string }>;
  confirmarAcesso(token: string): Promise<{ fase: string | null }>;
  sairDaLista(token: string): Promise<{ ok: boolean }>;
  reenviarConvite(email: string): Promise<{ message: string }>;
  founderEstado(): Promise<FounderEstado>;
  founderCodigo(body: { email: string; idioma: string; antiRobo?: string }): Promise<{ ok: boolean }>;
  founderConferir(body: { email: string; codigo: string }): Promise<FounderConferido>;
  founderPedir(body: FounderPedidoBody): Promise<FounderPedido>;
  founderPedido(id: string, segredo: string): Promise<FounderPedido>;
  founderCancelar(id: string, segredo: string): Promise<FounderPedido>;
  me(): Promise<Account>;
  gravarIdioma(idioma: string): Promise<void>;
  signOut(): Promise<void>;
  listShards(): Promise<readonly Shard[]>;
  listCharacters(shardId: string): Promise<readonly CharacterSummary[]>;
  creationCatalog(): Promise<CreationCatalog>;
  createCharacter(shardId: string, draft: CharacterDraft, idempotencyKey: string): Promise<CharacterSummary>;
  getCharacter(shardId: string, id: string): Promise<CharacterDetail>;
  updateAppearance(shardId: string, id: string, patch: CosmeticPatch): Promise<Appearance>;
  deleteCharacter(shardId: string, id: string, name: string): Promise<void>;
  concluirTutorial(shardId: string, id: string): Promise<void>;
  listFriends(): Promise<readonly Friend[]>;
  lookupFriend(name: string): Promise<FriendLookup>;
  addFriend(name: string): Promise<Friend>;
  removeFriend(characterId: string): Promise<void>;
  reportPlayer(name: string, reason: string): Promise<{ id: string }>;
  suporteAbrir(body: SuporteNovo): Promise<{ numero: number }>;
  suporteMeus(): Promise<readonly SuporteChamado[]>;
  suporteResumo(): Promise<{ precisaDeVoce: number }>;
  suporteResponder(numero: number, texto: string): Promise<void>;
  rankingTabela(tipo: RankingTipo, periodo: RankingPeriodo, ordem: 'kills' | 'kd', busca: string): Promise<RankingTabela>;
  rankingMinhas(): Promise<RankingMinhas>;
  rankingRegras(): Promise<RankingRegras>;
  /** O correio (techspec-correio §03): sempre do personagem ativo. */
  correio(): Promise<Correio>;
  correioResumo(): Promise<CorreioResumo>;
  correioLer(id: string): Promise<void>;
  correioReceber(id: string, anexo?: string): Promise<CorreioRecebido>;
  correioApagar(id: string): Promise<void>;
  correioApagarLidas(): Promise<number>;
  /** O botão Votar do chat (techspec-topidle-votos §04): sempre a conta da sessão. */
  topidleEstado(): Promise<TopIdleEstado>;
  /** Mais › Indicar amigos (techspec-indicacoes §04): o link da conta, os números e os indicados. */
  indicacoes(): Promise<Indicacao>;
  indicacoesTrocarCodigo(codigo: string): Promise<Indicacao>;
  /** A visita do site (techspec-indicacoes §03.1): devolve quem convidou, ou nulo. Nunca lança. */
  origemVisita(visita: OrigemVisita): Promise<string | null>;
  /**
   * O programa de parceiros (techspec-parceiros §03): sempre o parceiro da conta da sessão. A sessão é a do site dos
   * parceiros (cookie e CSRF próprios), que não abre o jogo.
   */
  parceiroEu(): Promise<ParceiroSessao | null>;
  parceiroEntrar(email: string, senha: string): Promise<ParceiroEntrada>;
  parceiroDoisFatores(desafio: string, codigo: string): Promise<ParceiroEntrada>;
  /** Troca a senha (obrigatória depois da provisória do e-mail). Devolve a sessão nova. */
  parceiroTrocarSenha(atual: string, nova: string): Promise<ParceiroEntrada>;
  parceiroSair(): Promise<void>;
  parceiroPainel(dias: 7 | 30 | 0): Promise<ParceiroPainel>;
  parceiroLinks(): Promise<ParceiroLinks>;
  parceiroCriarLink(canal: string, nome: string): Promise<ParceiroLinks>;
  /** A Wiki do site: público, sem login. */
  wikiDados(): Promise<WikiDados>;
  itemCatalog(): Promise<ItemCatalog>;
  getPack(): Promise<Pack>;
  loja(etag: string): Promise<LojaVitrine>;
  comprarLoja(body: { sku: string; preco: number; chave: string }): Promise<LojaCompra>;
  pedidosLoja(): Promise<readonly LojaPedido[]>;
  /** A aba Ouro: pacotes em reais, a semana usada e os Pix pendentes (techspec-pagamentos-mercado-pago §03.1). */
  lojaOuro(): Promise<LojaOuro>;
  pedirOuro(body: PedidoOuroBody): Promise<PagamentoOuro>;
  pedidoOuro(id: string): Promise<PagamentoOuro>;
  cancelarOuro(id: string): Promise<PagamentoOuro>;
  /** As compras de ouro com dinheiro da conta (as 50 últimas), para o histórico da loja. */
  historicoOuro(): Promise<readonly HistoricoOuro[]>;
  vip(): Promise<VipConta>;
  vipMostrar(mostrar: boolean): Promise<void>;
  /** Discord nas Configurações (techspec-discord-cargos §09). Nunca traz o id do Discord. */
  discordEstado(): Promise<DiscordEstado>;
  /** Começa o vincular: a URL do Discord (a página troca para ela). */
  discordVincular(): Promise<{ url: string }>;
  /** Acerta os cargos agora (uma vez por minuto). */
  discordAtualizar(): Promise<DiscordEstado>;
  discordDesvincular(): Promise<void>;
  /** A tela de entrada: mostra o "Entrar com Discord"? */
  discordLoginAtivo(): Promise<{ ativo: boolean }>;
  /** Entrar ou criar conta com o Discord: a URL do Discord (a página troca para ela). */
  discordEntrar(body: { serial?: string; idioma?: string }): Promise<{ url: string }>;
  npcShop(npcId: string): Promise<NpcShop>;
  /** Menu de vendas (techspec-vendas): a mochila com o preço que a cidade paga. */
  vendas(): Promise<VendaJanela>;
  vender(body: { itens: ReadonlyArray<{ itemId: string; qty: number }>; destino: VendaDestino; esperado: number }): Promise<VendaResultado>;
  recomprar(vendaId: string): Promise<RecompraResultado>;
  /** Várias de uma vez (a linha agrupada): tudo ou nada. */
  recomprarVarias(vendaIds: readonly string[]): Promise<RecompraResultado>;
  travarVenda(defId: string, travado: boolean): Promise<{ defId: string; travado: boolean }>;
  buyNpc(npcId: string, itens: ReadonlyArray<{ defId: string; qty: number }>): Promise<Pack>;
  /** Compras da cidade: as ofertas de todas as lojas de NPC da sala. */
  cidadeLoja(): Promise<CidadeLoja>;
  /** O carrinho das compras da cidade: linhas de várias lojas numa compra só. */
  comprarCidade(itens: ReadonlyArray<{ npcId: string; defId: string; qty: number }>): Promise<Pack>;
  getBanco(npcId: string): Promise<Banco>;
  guardarBanco(npcId: string, body: { itemId: string; qty?: number }): Promise<BancoMove>;
  sacarBanco(npcId: string, body: { bankItemId: string; qty?: number }): Promise<BancoMove>;
  depositarMoeda(npcId: string, body: { moeda: string; qtd?: number; tudo?: boolean }): Promise<BancoMove>;
  sacarMoeda(npcId: string, body: { moeda: string; qtd: number }): Promise<BancoMove>;
  listarModelos(npcId: string): Promise<HouseCatalogo>;
  comprarCasa(npcId: string, modeloId: string): Promise<HouseBuy>;
  minhaCasa(): Promise<MinhaCasa | null>;
  /** As casas erguidas no mapa (techspec-casas §03): lote, modelo e o centro do multi. */
  casasErguidas(mapaId: string): Promise<readonly CasaErguida[]>;
  /** Devolve a casa: o lote volta à venda, sem reembolso (techspec-casas §03.8). */
  devolverCasa(): Promise<void>;
  /** Gerenciar a casa (techspec-casas §04): itens presos e baús. Só o dono, dentro do lote. */
  casaItens(houseId: string): Promise<CasaItens>;
  prenderNaCasa(houseId: string, body: { itemId: string; qty?: number; x: number; y: number; z: number; giro?: number }): Promise<CasaItensMove>;
  soltarDaCasa(houseId: string, id: string): Promise<CasaItensMove>;
  /** Mover, subir, descer ou girar uma peça presa (techspec-marceneiro-e-oficios-em-casa §04.2). */
  moverNaCasa(houseId: string, id: string, body: { x: number; y: number; z: number; giro: number }): Promise<CasaItensMove>;
  /** Os números das ferramentas de ofício (§03.3). */
  oficiosRegras(): Promise<OficiosRegras>;
  /** Onde o personagem crafta o ofício agora (§05.4). */
  oficioOnde(oficio: string): Promise<OficioOnde>;
  /** As ferramentas de ofício da cidade num mapa (§05.2). */
  ferramentasDaCidade(mapa: string): Promise<readonly FerramentaDaCidade[]>;
  porBauNaCasa(houseId: string, ponto: { x: number; y: number; z: number; giro?: number }): Promise<CasaItensMove>;
  girarBauDaCasa(houseId: string, bauId: string): Promise<CasaItensMove>;
  recolherBauDaCasa(houseId: string, bauId: string): Promise<CasaItensMove>;
  guardarNoBau(houseId: string, bauId: string, body: { itemId: string; qty?: number }): Promise<CasaItensMove>;
  tirarDoBau(houseId: string, bauId: string, body: { id: string; qty?: number }): Promise<CasaItensMove>;
  /** Rótulo e "quem abre" do baú (techspec-casas §03.13): dono e co-dono. */
  configBauDaCasa(houseId: string, bauId: string, body: { rotulo?: string; perm?: number }): Promise<CasaItensMove>;
  /** Mover, subir, descer ou girar um baú (techspec-casa-interface-e-baus §01.4). */
  moverBauDaCasa(houseId: string, bauId: string, body: { x: number; y: number; z: number; giro: number }): Promise<CasaItensMove>;
  /** A placa da casa (§09): qualquer um lê. */
  placaDaCasa(houseId: string): Promise<CasaPlaca>;
  /** Renomear a casa (§09): só o dono. Vazio volta ao padrão. */
  nomeDaCasa(nome: string): Promise<MinhaCasa>;
  /** Põe alguém na lista de acesso, ou troca o papel: convidado (padrão), amigo, codono, banido. */
  convidar(nome: string, papel?: string): Promise<MinhaCasa>;
  /** Casa aberta: o visitante entra (banido nunca). */
  casaAberta(aberta: boolean): Promise<MinhaCasa>;
  desconvidar(nome: string): Promise<MinhaCasa>;
  oficinaMenu(oficio: Oficio): Promise<OficinaMenu>;
  oficinaFazer(oficio: Oficio, defId: string, material: string): Promise<OficinaFeito>;
  tailorMenu(): Promise<TailorMenu>;
  sew(defId: string): Promise<Craft>;
  fletchMenu(): Promise<FletchMenu>;
  carve(defId: string): Promise<Craft>;
  buyItem(defId: string, qty?: number, characterId?: string): Promise<PackItem>;
  sellItem(itemId: string, qty?: number): Promise<void>;
  mercadoCatalogo(query?: MercadoCatalogoQuery): Promise<MercadoCatalogo>;
  mercadoItem(defId: string, query?: MercadoItemQuery): Promise<MercadoItem>;
  mercadoOferta(lotId: string): Promise<MercadoOferta>;
  mercadoMeus(): Promise<MercadoMeus>;
  mercadoAnunciar(body: MercadoAnunciarBody): Promise<{ id: string }>;
  mercadoLance(lotId: string, teto: number): Promise<MercadoOferta>;
  mercadoComprarJa(lotId: string): Promise<{ id: string }>;
  mercadoCancelar(lotId: string): Promise<void>;
  mercadoRetirar(lotId: string): Promise<void>;
  equipItem(itemId: string): Promise<Pack>;
  unequipItem(itemId: string): Promise<Pack>;
  getTraje(): Promise<TrajeDto>;
  salvarTraje(doc: TrajeDoc): Promise<TrajeDto>;
  /** Guarda a Tinta prime da mochila: a cor passa a valer para todas as peças. */
  guardarTinta(itemId: string): Promise<TrajeDto>;
  /** Tira a tinta do guarda-roupa de volta para a mochila; a cor sai do traje. */
  retirarTinta(cor: string): Promise<TrajeDto>;
  /** A skin da mochila vai para o guarda-roupa (techspec-skins §06). */
  guardarSkin(itemId: string): Promise<TrajeDto>;
  /** A skin sai do guarda-roupa e vira item na mochila, para vender ou trocar. */
  extrairSkin(peca: string): Promise<TrajeDto>;
  /** Tira o selo "Nova" da peça exclusiva (o jogador clicou para ver). */
  marcarSkinVista(peca: string): Promise<void>;
  dropItem(itemId: string): Promise<Pack>;
  /** Separa `qty` da pilha numa linha nova (techspec-pilha-de-pecas §13). */
  separarItem(itemId: string, qty: number): Promise<SepararResultado>;
  /** Junta a pilha `itemId` na `destinoId`, da mesma chave. */
  juntarItem(itemId: string, destinoId: string): Promise<Pack>;
  getProfile(): Promise<Profile>;
  /** Inspecionar outro jogador da mesma sala: o set e o que o mundo já mostra dele. */
  inspecionar(nome: string): Promise<Inspecao>;
  lockSkill(skillId: string, lock: SkillLock): Promise<Profile>;
  getHotbar(): Promise<Hotbar>;
  setHotbar(slots: Hotbar): Promise<void>;
  getCombatIdle(): Promise<CombatIdleConfig>;
  setCombatIdle(config: CombatIdleConfig): Promise<void>;
  /**
   * Caçada espalhada (techspec-cacada-espalhada): a escolha própria do jogador. Hoje sem tela (o padrão do jogo é
   * ligado); fica para quando a opção voltar às Configurações.
   */
  getCacadaEspalhada(): Promise<boolean>;
  setCacadaEspalhada(ligado: boolean): Promise<void>;
  eventoAtual(): Promise<EventoAtual>;
  eventoAgenda(): Promise<EventoAgenda>;
  eventoInscrever(): Promise<void>;
  eventoDesistir(): Promise<void>;
  eventoAbrir(body: EventoAbrirBody): Promise<void>;
  eventoCancelar(): Promise<void>;
  /** Mais › Eventos › Chefe do Mundo (techspec-chefe-mundo §07). */
  eventoChefe(): Promise<Chefe>;
  /** Troca no Altar dos Fragmentos; `versao` é a do Altar que o jogador viu. */
  eventoAltarTrocar(oferta: string, versao: number): Promise<AltarTroca>;
  /** GM: o Balron agora (cor vazia = ao acaso; vida e duração vazias = as do documento). */
  eventoChefeIniciar(body: { variante?: string; vida?: number; duracaoMin?: number }): Promise<void>;
  /** GM: encerra o chefe vivo (foge, ninguém recebe). */
  eventoChefeEncerrar(): Promise<void>;
  /** Mais › Eventos › Pentagrama (techspec-pentagrama §09). */
  eventoPentagrama(): Promise<import('./evento').Penta>;
  /** GM: desperta uma ponta agora (cor vazia = a da vez). */
  eventoPentagramaIniciar(cor?: string): Promise<void>;
  /** GM: encerra a edição aberta e paga quem já fez o dano mínimo. */
  eventoPentagramaEncerrar(): Promise<void>;
  /** GM: busca pelo nome (q) ou, com `sala`, só quem está na sala do GM — nunca o shard inteiro (techspec-gm-menu §03). */
  listGmOnline(q?: string, take?: number, sala?: boolean): Promise<GmOnlinePagina>;
  gmOculto(ligado: boolean): Promise<{ ligado: boolean }>;
  /** GM: os sets de skins e o que o seu personagem já tem (techspec-skins §05). */
  gmSkins(): Promise<readonly GmSkinSet[]>;
  /** GM: pega o set inteiro para o guarda-roupa do seu personagem. */
  gmPegarSkin(set: string): Promise<GmSkinPegou>;
  /** GM: muda a hora de Britannia do servidor (techspec-dia-e-noite §04). */
  gmHora(hora: number, minuto: number): Promise<{ minuto: number; msPorMinuto: number }>;
  gmIr(characterId: string): Promise<GmIr>;
  gmKill(characterId: string): Promise<GmOnline>;
  gmRevive(characterId: string): Promise<GmOnline>;
  gmNotoriety(characterId: string, notoriety: Notoriety): Promise<GmOnline>;
  gmTeleport(characterId: string, x: number, y: number): Promise<GmOnline>;
  gmCity(characterId: string, cityId: string): Promise<GmOnline>;
  gmBank(characterId: string, prata?: number, ouro?: number): Promise<GmOnline>;
  gmSkills(characterId: string): Promise<GmSkills>;
  gmSetSkills(characterId: string, skills: readonly GmSkillValue[], skillCap?: number, atributos?: GmAtributosPedido): Promise<GmSkills>;
  listMobCatalog(): Promise<readonly MobResumo[]>;
  listGmMobs(): Promise<readonly GmMob[]>;
  gmSpawnMob(defId: string, x: number, y: number, range?: number): Promise<GmMob>;
  gmRemoveMob(shortId: number): Promise<GmMob>;
  getGuildMe(): Promise<Guild | null>;
  getGuildRules(): Promise<GuildRulesWire>;
  listGuilds(): Promise<readonly GuildListItem[]>;
  createGuild(name: string, tag: string): Promise<Guild>;
  leaveGuild(): Promise<void>;
  inviteGuild(name: string): Promise<void>;
  acceptGuild(): Promise<Guild>;
  declineGuild(): Promise<void>;
  applyGuild(id: string): Promise<void>;
  listGuildApplications(): Promise<readonly GuildApplication[]>;
  acceptGuildApplication(id: string): Promise<void>;
  refuseGuildApplication(id: string): Promise<void>;
  setGuildRank(name: string, rank: GuildRankWire): Promise<void>;
  passGuildLead(name: string): Promise<void>;
  kickGuild(name: string): Promise<void>;
  setGuildTitle(name: string, title: string): Promise<void>;
  setGuildMotd(text: string): Promise<void>;
  voteGuild(name: string): Promise<GuildVote>;
  setGuildTithe(percent: number): Promise<void>;
  withdrawGuildVault(amount: number): Promise<GuildVault>;
  getGuildVault(): Promise<GuildVault>;
  proposeAlliance(tag: string): Promise<void>;
  acceptAlliance(): Promise<void>;
  declineAlliance(): Promise<void>;
  leaveAlliance(): Promise<void>;
  proposeWar(tag: string, format: string, killsTo?: number, hours?: number): Promise<void>;
  acceptWar(): Promise<void>;
  declineWar(): Promise<void>;
  surrenderWar(): Promise<void>;
  gmCreateGuild(characterId: string, name: string, tag: string): Promise<GmOnline>;
  gmLeaveGuild(characterId: string): Promise<GmOnline>;
  gmGuildVault(characterId: string, amount: number): Promise<GmOnline>;
  getHunts(): Promise<HuntBoard>;
  estimateHunt(body: HuntStart): Promise<HuntEstimate>;
  startHunt(body: HuntStart): Promise<HuntBoard>;
  /** A área protegida para o menu Teleportar (techspec-cacada-protegida §02.1). */
  protegidaMenu(): Promise<ProtegidaMenu>;
  cancelHunt(): Promise<HuntBoard>;
  autoHunt(): Promise<HuntBoard>;
  /** Pausa o idle da caçada (techspec-hud-v3 §18): a IA para e o jogador assume, como no primeiro passo dele. */
  manualHunt(): Promise<HuntBoard>;
  /** Troca a regra da mochila cheia no meio da caçada (o chip). */
  regraHunt(body: { aoEncher?: HuntAoEncher; raroAbaixoDe?: number }): Promise<HuntBoard>;
  /** Fecha o cartão "Enquanto você esteve fora". */
  foraVistoHunt(): Promise<HuntBoard>;
  /** Coleta (techspec-coleta §06): o lugar, a ordem aberta e o resumo de quando acabou desconectado. */
  getColeta(): Promise<ColetaEstado>;
  iniciarColeta(recurso: 'madeira' | 'minerio'): Promise<ColetaEstado>;
  /** Recall e fim. `destino` = id de Viajar; sem ele, volta à cidade de onde saiu. */
  cancelarColeta(destino?: string): Promise<ColetaEstado>;
  tabelaCombate(etag: string): Promise<{ tabela: TabelaCombate; etag: string }>;
  esquemaMundo(): Promise<{ esquemaVersao: number }>;
  npcsDoMundo(etag: string, mapa: string): Promise<readonly NpcDoMundo[]>;
  manequinsDoMundo(etag: string, mapa: string): Promise<readonly ManequimDoMundo[]>;
  portaisDoMundo(etag: string, mapa: string): Promise<readonly PortalDoMundo[]>;
  ankhsDoMundo(etag: string, mapa: string): Promise<readonly AnkhDoMundo[]>;
  viagensDoMundo(etag: string): Promise<readonly ViagemDoMundo[]>;
}

export type { TabelaCombate };

export interface NpcDoMundo {
  id: string;
  nome: string;
  titulo: string;
  x: number;
  y: number;
  dir: number;
  corpo: string;
  roupa: string;
  pele: number;
  /** O fio das camadas pronto (armadura + traje), techspec-npc-editor. Vazio em servidor antigo: vale a `roupa`. */
  visual?: string;
  cabelo?: string;
  cabeloCor?: number;
  barba?: string;
  barbaCor?: number;
  mao?: string;
  esquerda?: string;
  alcance: number;
  temLoja: boolean;
  temCorretor: boolean;
  temBanco: boolean;
  lojaQtd?: number;
  lojaIcone?: string | null;
}

export interface ViagemPonto {
  x: number;
  y: number;
}

export interface ViagemDoMundo {
  id: string;
  mapa: string;
  x: number;
  y: number;
  rotulo: string;
  pontos?: readonly ViagemPonto[];
  /** `cidade` ou `local` (techspec-teleportar). */
  tipo?: string;
  /** O mapa tem guarda (proteção contra PK). Ausente = não se sabe. */
  guardas?: boolean;
}

export interface AnkhDoMundo {
  id: string;
  x: number;
  y: number;
  raio: number;
  rotulo: string;
}

export interface PortalDoMundo {
  id: string;
  x: number;
  y: number;
  destinoMapa: string;
  destX: number;
  destY: number;
  efeito: string;
  rotulo: string;
}

export interface ManequimDoMundo {
  id: string;
  x: number;
  y: number;
  variante: 'nordeste' | 'noroeste';
  dificuldadeArma: number;
  dificuldadeMagia: number;
  alcance: number;
  rotulo: string;
  uso: 'treino' | 'combate';
  defesa: number;
  resistenciaMagia: number;
}

export type GuildRankWire = 'lider' | 'comandante' | 'emissario' | 'membro' | 'recruta';

export interface GuildRulesWire {
  readonly const: {
    readonly nameMin: number;
    readonly nameMax: number;
    readonly tagMax: number;
    readonly titleMax: number;
    readonly warGold: number;
    readonly allianceCap: number;
    readonly inviteMinutes: number;
    readonly cooldownHours: number;
    readonly warKillsMax: number;
    readonly warHoursMax: number;
  };
  readonly perms: Readonly<Record<string, readonly GuildRankWire[]>>;
}

export interface GuildMember {
  readonly name: string;
  readonly rank: GuildRankWire;
  readonly title: string;
  readonly tithe: number;
  readonly joinedAt: string;
  readonly online: boolean;
  readonly vip?: boolean;
}

export interface GuildApplication {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
}

export interface GuildAlliance {
  readonly id: string;
  readonly leaderTag: string;
  readonly tags: readonly string[];
}

export interface GuildWar {
  readonly tag: string;
  readonly status: string;
  readonly format: string;
}

export interface GuildVoteRow {
  readonly name: string;
  readonly count: number;
}

export interface GuildVote {
  readonly applied: boolean;
  readonly votes: readonly GuildVoteRow[];
}

export interface GuildVaultEntry {
  readonly at: string;
  readonly name: string;
  readonly amount: number;
  readonly kind: string;
}

export interface GuildVault {
  readonly gold: number;
  readonly entries: readonly GuildVaultEntry[];
}

export interface GuildListItem {
  readonly id: string;
  readonly name: string;
  readonly tag: string;
  readonly members: number;
  readonly leader: string;
  readonly allianceTag: string;
}

export interface Guild {
  readonly id: string;
  readonly name: string;
  readonly tag: string;
  readonly motd: string;
  readonly gold: number;
  readonly rank: GuildRankWire;
  readonly title: string;
  readonly tithe: number;
  readonly canInvite: boolean;
  readonly canKickRecruit: boolean;
  readonly canKickMember: boolean;
  readonly canKickAny: boolean;
  readonly canRank: boolean;
  readonly canLead: boolean;
  readonly canMotd: boolean;
  readonly canVault: boolean;
  readonly canAlliance: boolean;
  readonly canWar: boolean;
  readonly canVote: boolean;
  readonly members: readonly GuildMember[];
  readonly alliance: GuildAlliance | null;
  readonly wars: readonly GuildWar[];
  readonly votes: readonly GuildVoteRow[];
  readonly online: number;
}

export interface GmMob {
  readonly shortId: number;
  readonly defId: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly range: number;
}

export interface GmOnline {
  readonly id: string;
  readonly name: string;
  readonly city: string;
  readonly dead: boolean;
  readonly you: boolean;
  readonly notoriety: Notoriety;
  readonly guildTag?: string;
  readonly guildName?: string;
  readonly oculto?: boolean;
  readonly canal?: number;
  readonly vipAteMs?: number;
}

export interface GmOnlinePagina {
  readonly itens: readonly GmOnline[];
  readonly total: number;
  readonly mostrando: number;
  readonly ocultoHabilitado?: boolean;
}

export interface GmIr {
  readonly cidade: string;
  readonly canal: number;
  readonly x: number;
  readonly y: number;
}

export interface GmSkill {
  readonly id: string;
  readonly name: string;
  readonly group: string;
  readonly value: number;
  readonly lock: SkillLock;
}

export interface GmSkills {
  readonly characterId: string;
  readonly name: string;
  readonly skills: readonly GmSkill[];
  readonly total: number;
  readonly skillCap: number;
  /** Força, Destreza e Inteligência base, o teto da soma e o de cada um (techspec-gm-atributos). */
  readonly atributos?: GmAtributos;
}

export interface GmAtributos {
  readonly for: number;
  readonly des: number;
  readonly int: number;
  readonly statCap: number;
  readonly teto: number;
}

export type GmAtributosPedido = Pick<GmAtributos, 'for' | 'des' | 'int'>;

export interface GmSkillValue {
  readonly id: string;
  readonly value: number;
}

export interface CatalogDef {
  readonly id: string;
  readonly name: string;
  readonly stackable: boolean;
  readonly weight: number;
  readonly price: number;
  readonly equipSlot: string;
  /** Material da linha (techspec-materiais); o ícone tinge pelo id. */
  readonly material?: string | null;
}

export interface ItemGroup {
  readonly id: string;
  readonly name: string;
  readonly items: readonly CatalogDef[];
}

export interface ItemCatalog {
  readonly groups: readonly ItemGroup[];
}

export interface MercadoSaldo {
  readonly cofre: number;
  readonly mochila: number;
}

export interface MercadoCatalogoQuery {
  readonly q?: string;
  readonly grupo?: string;
  readonly soOferta?: boolean;
  readonly ordem?: string;
}

export interface MercadoCatalogoLinha {
  readonly defId: string;
  readonly nome: string;
  readonly grupo: string;
  readonly stackable: boolean;
  readonly peso: number;
  readonly ar: number;
  readonly dano: string | null;
  readonly ofertas: number;
  readonly menorPreco: number | null;
  readonly lojaPreco: number | null;
  readonly lojaOficio: string | null;
}

export interface MercadoCatalogo {
  readonly itens: readonly MercadoCatalogoLinha[];
  readonly total: number;
  readonly comOferta: number;
  readonly saldo: MercadoSaldo;
}

export interface MercadoItemQuery {
  /** Filtro do cartão: um material da família da arma (`valorite`, `oak`). */
  readonly material?: string;
  readonly soImediata?: boolean;
  readonly soPago?: boolean;
  readonly ordem?: string;
}

export interface MercadoOfertaLinha {
  readonly id: string;
  readonly vendedor: string;
  readonly seu: boolean;
  readonly qty: number;
  readonly dur: number | null;
  readonly durMax: number | null;
  /** O item do lote: a base ou a variante (`katana@valorite`). */
  readonly defId: string;
  readonly nome: string;
  readonly material: string | null;
  readonly lances: number;
  readonly terminaEm: string;
  readonly preco: number;
  readonly proximoLance: number;
  readonly compraJa: number | null;
  /** HUD V3: com `compraJa == lanceMinimo` e sem lance, é preço fixo. */
  readonly lanceMinimo?: number;
}

export interface MercadoItem {
  readonly defId: string;
  readonly nome: string;
  readonly grupo: string;
  readonly stackable: boolean;
  readonly peso: number;
  readonly ar: number;
  readonly dano: string | null;
  readonly lojaPreco: number | null;
  readonly lojaOficio: string | null;
  readonly mediana: number | null;
  readonly vendidas: number;
  /** `metal`, `madeira` ou null: o filtro só oferece materiais dessa família. */
  readonly familia: 'metal' | 'madeira' | 'couro' | 'osso' | null;
  /** Materiais com oferta agora, na ordem da família. */
  readonly materiais: readonly string[];
  readonly ofertas: readonly MercadoOfertaLinha[];
  readonly saldo: MercadoSaldo;
}

export interface MercadoLance {
  readonly em: string;
  readonly nome: string;
  readonly teto: number;
  readonly seu: boolean;
}

export interface MercadoOferta {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly grupo: string;
  readonly qty: number;
  readonly dur: number | null;
  readonly durMax: number | null;
  readonly material: string | null;
  readonly peso: number;
  readonly ar: number;
  readonly dano: string | null;
  readonly vendedor: string;
  readonly seu: boolean;
  readonly podeCancelar: boolean;
  readonly terminaEm: string;
  readonly lanceMinimo: number;
  readonly preco: number;
  readonly proximoLance: number;
  readonly compraJa: number | null;
  readonly lojaPreco: number | null;
  readonly lojaOficio: string | null;
  readonly mediana: number | null;
  readonly historico: readonly MercadoLance[];
  readonly saldo: MercadoSaldo;
}

export interface MercadoMeus {
  readonly anunciosAtivos: number;
  readonly anunciosTeto: number;
  readonly anuncios: readonly MercadoMeuAnuncio[];
  readonly lances: readonly MercadoMeuLance[];
  readonly retirar: readonly MercadoRetirar[];
  readonly saldo: MercadoSaldo;
  readonly cofreUsado: number;
  readonly cofreVagas: number;
}

export interface MercadoMeuAnuncio {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly material?: string | null;
  readonly preco: number;
  readonly lances: number;
  readonly terminaEm: string;
  readonly estado: string;
  readonly lanceMinimo?: number;
  readonly compraJa?: number | null;
}

export interface MercadoMeuLance {
  readonly lotId: string;
  readonly defId: string;
  readonly nome: string;
  readonly material?: string | null;
  readonly teto: number;
  readonly preco: number;
  readonly vencendo: boolean;
  readonly terminaEm: string;
}

export interface MercadoRetirar {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly material?: string | null;
  readonly motivo: string;
}

export interface MercadoAnunciarBody {
  readonly itemId: string;
  readonly qty?: number;
  readonly lanceMinimo: number;
  readonly compraJa?: number | null;
  readonly horas: number;
}

export interface SepararResultado {
  readonly pack: Pack;
  readonly novoId: string;
}

export interface PackItem {
  readonly id: string;
  readonly defId: string;
  readonly name: string;
  readonly qty: number;
  readonly dur: number | null;
  readonly durMax: number | null;
  readonly weight: number;
  readonly equippedSlot: string | null;
  readonly material?: string | null;
  readonly abencoado?: boolean;
  /** Qualidade da peça (techspec-craft): 0 comum, 1 craftada, 2 excepcional. */
  readonly qualidade?: number;
  /** Quem fez, congelado na hora de fazer. */
  readonly feitoPor?: string | null;
}

/** A oficina de armas e armaduras: Ferraria (lingotes) e Arcos (toras). */
/** As oficinas de armas e armaduras; a Alfaiataria crafta o couro cravejado (Renato, 03/11/2026). */
export type Oficio = 'ferraria' | 'arcos' | 'alfaiataria';

export interface OficinaMaterial {
  readonly id: string;
  readonly nome: string;
  readonly cor: string;
  readonly padrao: boolean;
  readonly insumo: string;
  readonly insumoNome: string;
  readonly qty: number;
  readonly pericia: number;
  readonly pode: boolean;
  readonly forca: number;
}

export interface OficinaFicha {
  readonly material: string;
  readonly defId: string;
  readonly nome: string;
  readonly dado: string | null;
  readonly ar: number | null;
}

export interface OficinaReceita {
  readonly defId: string;
  readonly nome: string;
  readonly grupo: string;
  readonly slot: string;
  readonly qtd: number;
  readonly pericia: number;
  readonly bonus: number;
  readonly excepcional: number;
  readonly ativa: boolean;
  readonly fichas: readonly OficinaFicha[];
}

export interface OficinaRegras {
  readonly bonus: number;
  readonly excepcional: number;
  readonly chanceBase: number;
  readonly chancePorPonto: number;
  readonly chanceTeto: number;
  readonly chanceMestre: number;
}

export interface OficinaMenu {
  readonly oficio: Oficio;
  readonly pericia: number;
  readonly temFerramenta: boolean;
  readonly feitoPor: string;
  readonly materiais: readonly OficinaMaterial[];
  readonly receitas: readonly OficinaReceita[];
  readonly regras: OficinaRegras;
}

export interface OficinaFeito {
  readonly pack: Pack;
  readonly prontoEmMs: number;
  readonly itemId: string;
  readonly defId: string;
  readonly qualidade: number;
  readonly feitoPor: string;
  /** Feito com o bônus da oficina da casa; a oficina poupou o material (§05.3). */
  readonly casa?: boolean;
  readonly poupou?: boolean;
}

export interface TailorLinha {
  readonly defId: string;
  readonly name: string;
  readonly material: string;
  readonly materialName: string;
  readonly qtd: number;
  readonly rende: number;
  readonly tenho: number;
  readonly skill: number;
  readonly podeSkill: boolean;
}

export interface TailorMenu {
  readonly alfaiataria: number;
  readonly temKit: boolean;
  readonly pano: number;
  readonly receitas: readonly TailorLinha[];
}

export interface FletchInsumo {
  readonly defId: string;
  readonly name: string;
  readonly qtd: number;
  readonly tenho: number;
}

export interface FletchLinha {
  readonly defId: string;
  readonly name: string;
  readonly insumos: readonly FletchInsumo[];
  readonly rende: number;
  readonly skill: number;
  readonly podeSkill: boolean;
}

export interface FletchMenu {
  readonly arcos: number;
  readonly temFerramenta: boolean;
  readonly tora: number;
  readonly haste: number;
  readonly pena: number;
  readonly receitas: readonly FletchLinha[];
}

export interface LojaFrase {
  readonly pt: string;
  readonly en?: string;
  readonly es?: string;
}

export interface LojaOfertaJogo {
  readonly sku: string;
  readonly nome: LojaFrase;
  readonly descricao: LojaFrase;
  readonly preco: number;
  readonly selo: string | null;
  readonly entrega: { readonly tipo: string; readonly defId?: string; readonly abencoado?: boolean; readonly dias?: number };
  readonly aVendaDe: string | null;
  readonly aVendaAte: string | null;
  readonly limitePorPersonagem: number | null;
  readonly naJanela: boolean;
}

export interface LojaAbaJogo {
  readonly id: string;
  readonly nome: LojaFrase;
  readonly tipo: string;
  readonly provador: boolean;
  readonly intro: LojaFrase;
  readonly ofertas: readonly LojaOfertaJogo[];
}

export interface LojaVitrine {
  readonly aberta: boolean;
  readonly textos: { readonly titulo: LojaFrase; readonly fechada: LojaFrase; readonly reembolso: LojaFrase };
  readonly abas: readonly LojaAbaJogo[];
  readonly pacotes: readonly unknown[];
}

export interface LojaCompra {
  readonly id: string;
  readonly sku: string;
  readonly preco: number;
  readonly ouroBanco: number;
  readonly estado: string;
}

export interface LojaPedido {
  readonly id: string;
  readonly sku: string;
  readonly precoOuro: number;
  readonly estado: string;
  readonly origem: string;
  readonly criadoEm: string;
  readonly characterId: string;
}

export type VendaDestino = 'cofre' | 'mochila';
export type VendaCategoria = 'equipamento' | 'material' | 'consumivel';

export interface VendaItem {
  readonly itemId: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  /** O que a cidade paga por unidade; 0 quando não compra. */
  readonly preco: number;
  readonly compra: boolean;
  readonly travado: boolean;
  readonly equipado: boolean;
  /** Parte da pilha presa numa troca. */
  readonly preso: number;
  readonly categoria: VendaCategoria;
  readonly loot: boolean;
  readonly dur: number | null;
  readonly durMax: number;
  readonly peso: number;
}

export interface VendaRecompra {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly valor: number;
  /** ISO 8601. */
  readonly ate: string;
  /** Para juntar as peças iguais da mesma venda numa linha (techspec-venda-lote-e-pecas-agrupadas §06). */
  readonly dur?: number | null;
  readonly lote?: string | null;
  readonly qualidade?: number;
  readonly abencoado?: boolean;
}

export interface VendaJanela {
  readonly aberta: boolean;
  readonly motivo: 'longe' | 'cacando' | 'morto' | 'desligada' | 'mundo' | null;
  readonly cidade: string;
  readonly itens: readonly VendaItem[];
  readonly recompras: readonly VendaRecompra[];
  readonly regras: {
    readonly recompraMin: number;
    readonly confirmarTotal: number;
    readonly confirmarPeca: number;
    readonly destinoPadrao: VendaDestino;
  };
  readonly prataMochila: number;
  readonly prataCofre: number;
}

export interface VendaResultado {
  readonly total: number;
  readonly linhas: number;
  readonly destino: VendaDestino;
  readonly pack: Pack;
  readonly recompras: readonly VendaRecompra[];
}

export interface RecompraResultado {
  readonly defId: string;
  readonly qty: number;
  readonly valor: number;
  readonly pack: Pack;
  readonly recompras: readonly VendaRecompra[];
}

export interface Pack {
  readonly items: readonly PackItem[];
  readonly weight: number;
  readonly weightMax: number;
  readonly gold: number;
  readonly silver: number;
  readonly prataBanco?: number;
  readonly ouroBanco?: number;
  /** O visual que o servidor manda (equipamento + traje do guarda-roupa). Ausente no mock antigo. */
  readonly cloth?: string;
}

/** O guarda-roupa (techspec-guardaroupa §05). */
export interface TrajeSlot {
  readonly id: string; readonly nome: string;
  /** Lugar só das skins (braços, mãos, cajado): aparece quando o personagem tem uma skin para ele (techspec-skins). */
  readonly soSkin?: boolean;
}
export interface TrajePeca { readonly id: string; readonly nome: string; readonly slot: string; readonly soMulher: boolean; }
export interface TrajeCor {
  readonly id: string; readonly nome: string; readonly hue: number; readonly prime: boolean;
  readonly raridade?: 'comum' | 'rara' | 'epica' | 'lendaria' | null;
  /** De todos desde o começo (a Natural e três básicas); as outras pedem a tinta guardada. */
  readonly livre?: boolean;
}
export interface TrajeEscolha { readonly peca: string | null; readonly cor: string; }
export interface TrajeMostrar { readonly elmo: boolean; readonly peito: boolean; readonly luvas: boolean; readonly pernas: boolean; }
export interface TrajeDoc {
  readonly usar: boolean;
  readonly mostrar: TrajeMostrar;
  readonly pecas: Readonly<Record<string, TrajeEscolha>>;
}
export interface TrajeDto {
  readonly slots: readonly TrajeSlot[];
  readonly pecas: readonly TrajePeca[];
  readonly cores: readonly TrajeCor[];
  readonly feminino: boolean;
  readonly traje: TrajeDoc;
  readonly salvo: boolean;
  /** As cores prime com a tinta guardada. */
  readonly guardadas: readonly string[];
  /** As peças exclusivas (a Capa do Balron, techspec-chefe-mundo §06): cor fixa e bônus. */
  readonly exclusivas?: readonly TrajeExclusiva[];
}

export interface TrajeExclusiva {
  readonly id: string;
  readonly nome: string;
  readonly slot: string;
  readonly hue: number;
  /** Força, Destreza e Inteligência com a peça vestida e o traje ligado. */
  readonly bonus: number;
  readonly origem: string;
  /** Skin (techspec-skins): o set dela (`natal`). Cor pronta na folha, sem tinta nem bônus. */
  readonly set?: string | null;
  readonly soMulher?: boolean;
  /** Skin que aceita as cores do guarda-roupa (os mantos); sem isso, a cor está pronta na folha (o Natal). */
  readonly tingivel?: boolean;
  /** Peça do personagem (o Shroud Robe): não se extrai para item. */
  readonly presa?: boolean;
  /** Chegou e o jogador ainda não clicou para ver: o guarda-roupa mostra o selo "Nova". */
  readonly nova?: boolean;
}

/** Um set de skins no menu do GM (techspec-skins §05): a foto é a peça `icone`. */
export interface GmSkinSet {
  readonly id: string;
  readonly nome: string;
  readonly icone: string;
  readonly pecas: readonly { readonly id: string; readonly nome: string; readonly slot: string; readonly soMulher: boolean; readonly tem: boolean }[];
}

export interface GmSkinPegou {
  readonly set: string;
  readonly novas: readonly string[];
  readonly atual: GmSkinSet;
}

export interface Craft {
  readonly pack: Pack;
  readonly prontoEmMs: number;
  /** Feito com o bônus da oficina da casa; a oficina poupou o material (§05.3). */
  readonly casa?: boolean;
  readonly poupou?: boolean;
}

export interface NpcShopItem {
  readonly defId: string;
  readonly nome: string;
  readonly preco: number;
  readonly peso: number;
}

export interface BancoItem {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly dur: number | null;
  readonly durMax: number | null;
  readonly material?: string | null;
}

/** A seção Discord das Configurações. `ligado` = o servidor tem o Discord configurado. */
export interface DiscordEstado {
  ligado: boolean;
  vinculado: boolean;
  /** Os nomes dos cargos que o bot deu (VIP, Fundador Lorde…). */
  cargos: readonly string[];
  pendente: boolean;
  /** `fora_do_servidor`, `entrar_falhou`, `discord_indisponivel` ou `recusado`. */
  erro: string | null;
  sincronizadoEm: number | null;
  /** O convite do servidor (Configurações › Redes no console). */
  convite: string;
}

export interface VipConta {
  readonly ate: number;
  readonly mostrar: boolean;
  readonly ativo: boolean;
  readonly banco: number;
  readonly precos: Record<string, number>;
}

export interface Banco {
  readonly prata: number;
  readonly ouro: number;
  readonly vagas: number;
  readonly vagasMax: number;
  readonly peso: number;
  readonly pesoMax: number;
  readonly itens: readonly BancoItem[];
}

export interface BancoMove {
  readonly banco: Banco;
  readonly pack: Pack;
}

/** Uma oferta das compras da cidade: quem vende, o item, o preço do balcão e o essencial do item. */
export interface CidadeItem {
  readonly npcId: string;
  readonly npcNome: string;
  /** Chave i18n do ofício (npc.titulo.ferreiro). */
  readonly npcTitulo: string;
  readonly defId: string;
  readonly nome: string;
  readonly preco: number;
  readonly peso: number;
  /** weapon, armor, shield, ammo, potion, misc, mount, reagent… */
  readonly grupo: string;
  readonly slot: string;
  readonly ar: number;
  readonly dano: string | null;
  readonly maos: number;
  readonly forca: number;
  readonly alcance: number | null;
  readonly empilha: boolean;
  /** Mobília (techspec-marceneiro-e-oficios-em-casa §03): 0 comum, 1 incomum … 4 lendário. */
  readonly raridade?: number;
  readonly conjunto?: string | null;
  /** Só a ferramenta de ofício lendária: ferraria, alfaiataria, arcos, treino. */
  readonly oficio?: string | null;
  /** A ferramenta que o personagem já tem (uma por personagem). */
  readonly jaTem?: boolean;
  readonly luz?: boolean;
}

/** As compras da cidade (Renato, 29/10/2026): tudo o que as lojas de NPC da sala vendem. */
export interface CidadeLoja {
  readonly mapa: string;
  /** A Força do personagem: o filtro "só o que posso usar". */
  readonly forca: number;
  readonly itens: readonly CidadeItem[];
}

export interface NpcShop {
  readonly npcId: string;
  readonly moeda: string;
  readonly itens: readonly NpcShopItem[];
}

export type SkillLock = 'raise' | 'lower' | 'locked';

/** Seis posições. `null` é slot vazio. A ordem é a da tela. */
export type Hotbar = readonly (string | null)[];

export interface ProfileSkill {
  readonly id: string;
  readonly name: string;
  readonly group: string;
  readonly value: number;
  readonly lock: SkillLock;
}

export interface Profile {
  readonly name: string;
  readonly profession: string;
  readonly templates: readonly string[];
  readonly notoriety: string;
  readonly homeCity: string;
  readonly stats: {
    readonly strength: number;
    readonly dexterity: number;
    readonly intelligence: number;
    readonly strengthBase: number;
    readonly dexterityBase: number;
    readonly intelligenceBase: number;
    readonly sum: number;
    readonly cap: number;
    /** A parte da poção no efetivo (techspec-pocoes). */
    readonly strengthBuff?: number;
    readonly dexterityBuff?: number;
    readonly intelligenceBuff?: number;
  };
  readonly vitals: {
    readonly hits: number;
    readonly hitsMax: number;
    readonly mana: number;
    readonly manaMax: number;
    readonly stam: number;
    readonly stamMax: number;
    readonly fadiga?: number;
    readonly fadigaMax?: number;
  };
  readonly carry: {
    readonly weight: number;
    readonly weightMax: number;
  };
  readonly skills: readonly ProfileSkill[];
  readonly skillTotal: number;
  readonly skillCap: number;
  readonly combat: {
    readonly ar: number;
    readonly damageMin: number | null;
    readonly damageMax: number | null;
    readonly velAtaqueBp: number;
    readonly velMagiaBp: number;
    readonly swingSegundos: number;
    readonly cicloMagiaSegundos: number;
    readonly swingSegundosBase: number;
    readonly cicloMagiaSegundosBase: number;
    readonly conjunto: {
      readonly familia: string;
      readonly pecas: number;
      readonly pecasExigidas: number;
      readonly completo: boolean;
    } | null;
    readonly itens?: readonly {
      readonly nome: string;
      readonly velAtaqueBp: number;
      readonly velMagiaBp: number;
      readonly strBonus: number;
      readonly dexBonus: number;
      readonly intBonus: number;
    }[];
    /** 1 a pé. Montaria e, no futuro, itens multiplicam. */
    readonly velMovimento?: number;
  };
  readonly guildTag?: string;
  readonly guildName?: string;
  /** Selo de fundador (techspec-correio §05): 0 nenhum, 1 bronze, 2 prata, 3 ouro. */
  readonly selo?: number;
}

export type HuntPostura = 'cautelosa' | 'firme' | 'temeraria';
export interface HuntMob {
  readonly defId: string;
  readonly quantidade: number;
  readonly ninhos: number;
  readonly respawnMin: number;
  readonly respawnMax: number;
  readonly resumo: MobResumo;
}

export interface MobResumo {
  readonly defId: string;
  readonly nome: string;
  readonly hits: number;
  readonly ar: number;
  readonly magicResist: number;
  readonly poisonImmune: boolean;
  readonly permiteParalisia?: boolean;
  /** A folha de sprite do mob (o retrato); sem ela, vale o defId. */
  readonly aparencia?: string;
  /** O dado de dano do mob, já resolvido (HUD V3). */
  readonly danoMin?: number;
  readonly danoMax?: number;
}

export interface HuntLoot {
  readonly defId: string;
  readonly qtdMin: number;
  readonly qtdMax: number;
  readonly chance: number;
  /** Quem deixa cair e quanto (HUD V3 §09). */
  readonly porMob?: readonly HuntLootMob[];
}

export interface HuntLootMob {
  readonly mobId: string;
  readonly qtdMin: number;
  readonly qtdMax: number;
  readonly chance: number;
}

export interface HuntSkillEmJogo {
  readonly id: string;
  readonly motivo: string;
  readonly pontosNaViagem: number;
}

export interface HuntRecord {
  readonly segundos: number;
  readonly prataLiquida: number;
  readonly semMorrer: boolean;
  readonly quando: string;
}

/**
 * O bastante para a lista comparar caçadas sem abrir uma por uma. Vem da mesma
 * estimativa que o board já calcula por caçada, na postura firme.
 */
export interface HuntResumo {
  readonly prataPorHora: number;
}

export interface HuntView {
  readonly id: string;
  readonly aba: string;
  readonly mapa: string;
  readonly nivel: number;
  readonly risco: string;
  readonly skillSugerida: string;
  readonly skillSugeridaValor: number;
  readonly skillAtual: number;
  readonly inimigos: readonly HuntMob[];
  readonly loot: readonly HuntLoot[];
  readonly skillsEmJogo: readonly HuntSkillEmJogo[];
  readonly filtro: readonly string[];
  readonly recorde: HuntRecord | null;
  readonly temGuarda: boolean;
  readonly resumo: HuntResumo;
  /** A área protegida desta caçada (techspec-cacada-protegida): quanto rende a menos, em %. Nulo: não tem agora. */
  readonly protegida?: HuntProtegida | null;
}

/** Redução da área protegida, em %: 25 = rende 75%. Item com chance abaixo de `limiteRaro` usa `raros`. */
export interface HuntProtegida {
  readonly xp: number;
  readonly prata: number;
  readonly itens: number;
  readonly raros: number;
  readonly limiteRaro: number;
}

/** PvP (como sempre) ou protegida (non-PvP, techspec-cacada-protegida). */
export type HuntArea = 'pvp' | 'protegida';

/** A área protegida no menu Teleportar: ligada, a redução do texto e os mapas onde ela não vale. */
export interface ProtegidaMenu {
  readonly ativa: boolean;
  readonly xp: number;
  readonly saque: number;
  readonly mapasSem: readonly string[];
}

export interface HuntCarry {
  readonly prata: number;
  readonly peso: number;
  readonly pesoMax: number;
  readonly bandagem: number;
}

/** Os dois modos de conduzir a caçada (D18). */
export type HuntModo = 'automatico' | 'manual';

/** O que fazer quando um jogador aparece (D21). Escolhido antes de sair. */
export type HuntAoVer = 'batalhar' | 'correr';

export interface HuntOrder {
  readonly huntId: string;
  readonly postura: string;
  readonly filtro: readonly string[];
  readonly startedAt: string;
  readonly terminaEm: string;
  readonly modo: HuntModo;
  readonly aoVerJogador: HuntAoVer;
  /** A contagem desta caçada (HUD V3 §09): abates por mob, prata e itens que entraram. */
  readonly abates?: Readonly<Record<string, number>> | null;
  readonly prata?: number;
  readonly saque?: Readonly<Record<string, number>> | null;
  /** Mochila cheia (techspec-cacada-venda): guardar no cofre (todos) ou vender (VIP), e o slider dos raros. */
  readonly aoEncher?: HuntAoEncher;
  readonly raroAbaixoDe?: number;
  /** A pausa da mochila cheia em andamento, ou null. */
  readonly pausa?: HuntPausa | null;
  readonly cofre?: HuntCofreConta | null;
  readonly venda?: HuntVendaConta | null;
  /** `pvp` ou `protegida` (techspec-cacada-protegida). */
  readonly area?: HuntArea;
}

/** O que fazer com a mochila cheia na caçada. */
export type HuntAoEncher = 'cofre' | 'vender';

/** `recall-ida`, `guardando`, `recall-volta` ou `vendendo`; `ateMs` em ms de época. */
export interface HuntFase {
  readonly codigo: string;
  readonly ateMs: number;
}

/** `pendente` (esperando o banco), `cofre` (a viagem) ou `venda`. */
export interface HuntPausa {
  readonly tipo: 'pendente' | 'cofre' | 'venda';
  readonly fases: readonly HuntFase[];
}

export interface HuntCofreConta {
  readonly itens: number;
  readonly prata: number;
  readonly viagens: number;
  readonly saque: Readonly<Record<string, number>>;
}

export interface HuntVendaConta {
  readonly prata: number;
  readonly itens: number;
  readonly vendas: number;
  readonly saque: Readonly<Record<string, number>>;
}

/** Os números da regra: o Recall é o ciclo da magia, configurável. `banco` é a cidade do cofre (a natal). */
export interface HuntRegras {
  readonly recallS: number;
  readonly guardarS: number;
  readonly vendaS: number;
  readonly recompraMin: number;
  readonly raroPadrao: number;
  readonly raroMin: number;
  readonly raroMax: number;
  readonly banco: string;
}

/** "Enquanto você esteve fora": o tempo resolvido no login. `fim`: null (segue), tempo, cofre ou morte. */
export interface HuntFora {
  readonly segundos: number;
  readonly mortos: number;
  readonly prata: number;
  readonly viagens: number;
  readonly guardados: number;
  readonly vendas: number;
  readonly prataDaVenda: number;
  readonly vendidos: number;
  readonly vipVenceu: boolean;
  readonly fim: 'tempo' | 'cofre' | 'morte' | null;
  readonly cidade: string;
  readonly huntId: string;
}

export interface ColetaChance {
  readonly material: string;
  readonly tora: string;
  readonly skillMin: number;
  /** Chance desta tora numa tentativa (0..1), já com o acerto. */
  readonly chance: number;
  readonly trancada: boolean;
}

export interface ColetaLugar {
  readonly id: 'madeira' | 'minerio';
  readonly mapa: string;
  readonly nome: string;
  readonly lotacao: number;
  readonly vagas: number;
  /** Pontos de coleta (árvores ou veios). */
  readonly arvores: number;
  readonly chances: readonly ColetaChance[];
  readonly falha: number;
  /** A skill do personagem neste recurso (Lenhador ou Mineração). */
  readonly skill: number;
  /** A melhor ferramenta deste recurso que ele tem (machadinha ou picareta), ou null. */
  readonly ferramenta: string | null;
  /** O bônus dela (a de ouro: 10). As chances já vêm com ele. */
  readonly bonus: number;
}

export interface ColetaOrdem {
  readonly recurso: string;
  readonly startedAt: string;
  readonly terminaEm: string;
  readonly tentativas: number;
  readonly acertos: number;
  readonly toras: Readonly<Record<string, number>>;
  readonly parado: string | null;
  readonly origem: string;
  readonly skillInicio: number;
  readonly naFloresta: boolean;
}

export interface ColetaResumo {
  readonly recurso: string;
  readonly startedAt: string;
  readonly encerradaEm: string;
  readonly tentativas: number;
  readonly acertos: number;
  readonly toras: Readonly<Record<string, number>>;
  readonly skillInicio: number;
  readonly skillFim: number;
  readonly parado: string | null;
}

export interface ColetaEstado {
  readonly lugares: readonly ColetaLugar[];
  readonly ordem: ColetaOrdem | null;
  readonly resumo: ColetaResumo | null;
  /**
   * A melhor ferramenta do recurso da ordem aberta (ou da madeira, sem ordem): `hatchet`, `golden-hatchet`,
   * `pickaxe`, `golden-pickaxe`, ou null. Não se gasta. Cada lugar traz a sua.
   */
  readonly machadinha: string | null;
  /** O bônus de Lenhador dela (a de ouro: 10). As chances já vêm com ele. */
  readonly bonus: number;
  readonly cofreUsadas: number;
  readonly cofreVagas: number;
  readonly skill: number;
  readonly vip: boolean;
  readonly regras: { readonly tentativaSeg: number; readonly golpes: number; readonly porAcerto: number; readonly vipColeta: number; readonly vipGanho: number };
}

/** O gump ao vivo: o evento `co` da sala (techspec-coleta §07). */
export interface ColetaAoVivo {
  readonly ativa: boolean;
  /** 0 ida, 1 andando, 2 cortando, 3 parado, 4 volta. */
  readonly fase: number;
  readonly arvore: number;
  readonly skill: number;
  readonly skillInicio: number;
  readonly tentativas: number;
  readonly acertos: number;
  readonly toras: Readonly<Record<string, number>>;
  readonly terminaEm: number;
  /** O bônus de Lenhador da machadinha (a de ouro: 10). */
  readonly bonus: number;
  /** A machadinha que ele segura. */
  readonly ferramenta: string;
  readonly parado: string;
  readonly proximaEm: number;
  readonly tentativaMs: number;
  readonly ultimo: number;
  readonly ultimaTora: string;
  readonly ultimaQtd: number;
  readonly origem: string;
  /** O recurso: `madeira` ou `minerio`. */
  readonly recurso: string;
  /** Relógio local de quando chegou (para animar o anel). */
  readonly recebidoEm: number;
}

export interface HuntBoard {
  readonly cacadas: readonly HuntView[];
  readonly voce: HuntCarry;
  readonly ordem: HuntOrder | null;
  readonly morto: boolean;
  readonly precoBandagem: number | null;
  readonly vip?: boolean;
  readonly regras?: HuntRegras | null;
  readonly fora?: HuntFora | null;
}

export interface HuntEstimate {
  readonly segundosAteVigor: number;
  readonly segundosAteMochila: number;
  readonly prataPorHora: number;
  readonly pesoPorHora: number;
  readonly mortesPorHora: number;
  readonly pontosDaViagem: number;
  readonly skillsEmJogo: readonly HuntSkillEmJogo[];
  readonly recuarAbaixoDe: number;
  readonly maxInimigos: number;
  readonly curarAteCheio: boolean;
  readonly segundosForaDeLuta: number;
  readonly tempoDeLuta: number;
}

export interface HuntStart {
  huntId: string;
  /** HUD V3: o servidor ignora e usa sempre firme. */
  postura?: HuntPostura;
  filtro: readonly string[];
  modo?: HuntModo;
  aoVerJogador?: HuntAoVer;
  aoEncher?: HuntAoEncher;
  raroAbaixoDe?: number;
  /** PvP (padrão) ou protegida. O grupo vai na área do líder. */
  area?: HuntArea;
}

export type HouseClasse = 'P' | 'M' | 'G' | 'L';

/** Um modelo de casa no corretor (techspec-casas §02). O lote é alocado pela classe na compra. */
export interface HouseModelo {
  readonly id: string;
  readonly classe: HouseClasse;
  readonly largura: number;
  readonly altura: number;
  readonly andares: number;
  readonly terraco: boolean;
  readonly preco: number;
  /** O que sai agora: 0 enquanto `casas.gratis` estiver ligado. */
  readonly precoAgora: number;
  /** Prata por semana; ainda não cobrado. */
  readonly aluguel: number;
  readonly presos: number;
  readonly baus: number;
  readonly manequins: number;
  readonly convidados: number;
  /** Lotes livres da classe neste vilarejo. */
  readonly livres: number;
}

export interface CasaErguida {
  readonly id: string;
  readonly numero: number;
  readonly modelo: string;
  readonly ox: number;
  readonly oy: number;
  /** O que quem passa vê (techspec-casas §04): itens presos (só o molde) e baús. */
  readonly presos?: readonly { readonly defId: string; readonly x: number; readonly y: number; readonly z: number; readonly giro?: number; readonly bloqueia?: boolean }[];
  readonly baus?: readonly { readonly x: number; readonly y: number; readonly z: number; readonly giro: number; readonly defId?: string }[];
  /** O papel de quem pede nesta casa: dono, codono, amigo, convidado, visitante, banido. */
  readonly papel?: string;
  /** Casa aberta: a porta abre também para o visitante. */
  readonly aberta?: boolean;
  /** A placa (techspec-casa-interface-e-baus §09): o nome que o dono deu (null = "Casa de {dono}") e o personagem dono. */
  readonly nome?: string | null;
  readonly dono?: string | null;
}

/** A placa da casa (techspec-casa-interface-e-baus §09), como no UO: pública no shard. */
export interface CasaPlaca {
  readonly id: string;
  readonly nome: string | null;
  readonly dono: string | null;
  readonly modelo: string;
  readonly aberta: boolean;
  readonly papel: string;
}

export interface CasaPreso {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** O giro da peça (0 a 3) e a altura dela em z (techspec-marceneiro-e-oficios-em-casa §04). */
  readonly giro?: number;
  readonly altura?: number;
}

/** Uma ferramenta de ofício presa (§04.1): o bônus vale para quem pede? `motivo` é a chave i18n de por que não. */
export interface CasaOficio {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly oficio: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly giro: number;
  readonly altura: number;
  readonly bonusAtivo: boolean;
  readonly motivo: string | null;
}

export interface CasaBauItem {
  readonly id: string;
  readonly defId: string;
  readonly nome: string;
  readonly qty: number;
  readonly dur: number | null;
  readonly durMax: number | null;
  readonly qualidade: number;
  readonly feitoPor: string | null;
}

export interface CasaBau {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly giro: number;
  readonly itens: readonly CasaBauItem[];
  /** Quem abre: 1 convidados, 2 amigos, 3 dono e co-donos. */
  readonly perm: number;
  readonly rotulo: string | null;
  /** Quem pediu não abre este baú: os itens vêm vazios. */
  readonly trancado: boolean;
  /** O baú do catálogo da mobília (techspec-casa-interface-e-baus §01): o comum grátis ou um comprado. */
  readonly defId?: string;
  /** As vagas deste baú (pela config do item) e quantas estão ocupadas (todos com papel veem, mesmo trancado). */
  readonly vagas?: number;
  readonly usadas?: number;
}

export interface CasaItens {
  readonly presos: readonly CasaPreso[];
  readonly baus: readonly CasaBau[];
  readonly maxPresos: number;
  readonly maxBaus: number;
  readonly vagasPorBau: number;
  readonly alcance: number;
  /** O papel de quem pede: dono, codono, amigo, convidado. */
  readonly papel: string;
  readonly oficios?: readonly CasaOficio[];
  readonly maxOficios?: number;
  /** O alto do muro em z: o topo de nenhuma peça passa dele. */
  readonly alturaDoMuro?: number;
  /** O teto de cada baú por casa (0 = só o limite do modelo) e o % do aviso âmbar dos contadores. */
  readonly tetosDeBau?: Readonly<Record<string, number>>;
  readonly quase?: number;
  /** O nome da casa (null = o padrão). */
  readonly nome?: string | null;
}

/** Os números de uma ferramenta de ofício (§03.3), do console. */
export interface FerramentaRegras {
  readonly oficio: string;
  readonly defId: string;
  readonly ativa: boolean;
  readonly preco: number;
  readonly ganho: number;
  readonly tempo: number;
  readonly excepcional: number;
  readonly economia: number;
  readonly atributo: number;
}

export interface OficiosRegras {
  readonly ferramentas: readonly FerramentaRegras[];
  readonly distancia: number;
  readonly papeis: readonly string[];
}

/** Onde o personagem crafta o ofício agora (§05.4). */
export interface OficioOnde {
  readonly oficio: string;
  readonly perto: boolean;
  readonly onde: 'casa' | 'cidade' | 'fora';
  readonly distancia: number | null;
  readonly fx: number | null;
  readonly fy: number | null;
  readonly fz: number | null;
  readonly defId: string | null;
  readonly motivo: string | null;
  readonly bonus: FerramentaRegras | null;
  readonly distanciaMax: number;
}

/** Uma ferramenta de ofício da cidade (§05.2): sem dono e sem bônus; só perto dela se crafta fora da casa. */
export interface FerramentaDaCidade {
  readonly oficio: string;
  readonly defId: string;
  readonly x: number;
  readonly y: number;
  readonly giro: number;
  /** Bloqueia o passo (padrão); o console pode deixar a peça atravessável. */
  readonly bloqueia?: boolean;
}

export interface CasaItensMove {
  readonly casa: CasaItens;
  readonly pack: Pack;
}

export interface MinhaCasa {
  readonly id: string;
  readonly numero: number;
  readonly classe: HouseClasse;
  readonly modelo: string | null;
  readonly mapa: string;
  readonly lugar: string;
  readonly maxConvidados: number;
  readonly convidados: readonly string[];
  /** A casa da rua na frente do lote: para onde o Teleportar → Minha casa leva. */
  readonly soleiraX: number;
  readonly soleiraY: number;
  /** A lista de acesso com o papel (techspec-casas §03.13). Ausente no servidor antigo. */
  readonly acesso?: readonly { readonly nome: string; readonly papel: string }[];
  readonly aberta?: boolean;
  readonly maxBanidos?: number;
}

export interface HouseCatalogo {
  readonly npcId: string;
  readonly lugar: string;
  readonly modelos: readonly HouseModelo[];
  readonly minha: MinhaCasa | null;
}

export interface HouseBuy {
  readonly minha: MinhaCasa;
  readonly pack: Pack | null;
}

export function normalizeName(raw: string): string {
  return raw.normalize('NFKC').trim().replace(/ {2,}/g, ' ');
}

/* ------------------------------------------------------------------ suporte */

export type SuporteTipo = 'bug' | 'sugestao';
export type SuporteFrequencia = 'sempre' | 'as-vezes' | 'uma-vez';
/** A situação como o jogador vê. A equipe não responde um a um: não há "resolvido" nem "recusado". */
export type SuporteSituacao = 'recebido' | 'analise' | 'contato' | 'analisado';

export const SUPORTE_ASSUNTOS = [
  'combate', 'magia', 'itens', 'oficios', 'mercado', 'banco', 'guildas', 'duelo',
  'mapa', 'interface', 'chat', 'loja', 'conta', 'desempenho', 'textos', 'outro'
] as const;
export type SuporteAssunto = typeof SUPORTE_ASSUNTOS[number];

/** O que vai junto do chamado. O jogador vê cada valor antes de enviar. */
export interface SuporteContexto {
  lugar?: string;
  mapa?: string;
  x?: number;
  y?: number;
  canal?: number;
  cacando?: boolean;
  postura?: string;
  grupo?: boolean;
  duelo?: boolean;
  modo?: 'pc' | 'cel' | 'tablet';
  telaW?: number;
  telaH?: number;
  dpr?: number;
  aparelhoW?: number;
  aparelhoH?: number;
  toque?: boolean;
  idioma?: string;
  fuso?: string;
  memoria?: number;
  nucleos?: number;
  gpu?: string;
  latMedia?: number;
  latPior?: number;
  quedas?: number;
  rede?: string;
  downlink?: number;
  fpsMedio?: number;
  fpsPior?: number;
  sessaoMin?: number;
  versaoFront?: string;
  mundo?: string;
  erros?: string[];
  diario?: { t: string; m: string }[];
}

export interface SuporteNovo {
  tipo: SuporteTipo;
  assunto: SuporteAssunto;
  titulo: string;
  relato: string;
  frequencia: SuporteFrequencia | null;
  plataforma: 'pc' | 'cel' | 'tablet';
  idioma: string;
  versaoFront: string;
  versaoMundo: string | null;
  contexto: SuporteContexto;
  /** WebP ou JPEG em base64, até 300 KB. */
  captura: string | null;
}

export interface SuporteConversa {
  readonly de: 'equipe' | 'voce';
  readonly texto: string;
  readonly em: string;
}

export interface SuporteChamado {
  readonly numero: number;
  readonly tipo: SuporteTipo;
  readonly assunto: SuporteAssunto;
  readonly titulo: string;
  readonly relato: string;
  readonly frequencia: SuporteFrequencia | null;
  readonly situacao: SuporteSituacao;
  readonly criadoEm: string;
  readonly analiseEm: string | null;
  readonly pedidoEm: string | null;
  readonly finalizadoEm: string | null;
  readonly conversa: readonly SuporteConversa[];
}

/* ------------------------------------------------------------------ rankings */

export type RankingTipo = 'individual' | 'guildas' | 'pk' | 'ub' | 'duelo';
export type RankingPeriodo = 'semana' | 'temporada' | 'geral';

export interface RankingTemporada {
  numero: number;
  inicio: string;
  fim: string;
}

/** Uma linha de qualquer ranking; os campos que não se aplicam vêm nulos. */
export interface RankingLinha {
  pos: number;
  id: string;
  nome: string;
  tag: string;
  /** 0 inocente, 1 criminoso, 2 assassino (a de agora). */
  noto: number;
  kills?: number | null;
  mortes?: number | null;
  killsGuerra?: number | null;
  inocentes?: number | null;
  contagens?: number | null;
  membros?: number | null;
  pontuaram?: number | null;
  guerrasV?: number | null;
  guerrasD?: number | null;
  elo?: number | null;
  delta?: number | null;
  vitorias?: number | null;
  derrotas?: number | null;
  empates?: number | null;
  sequencia?: number | null;
  provisorio?: boolean;
  ultima?: string | null;
  voce?: boolean;
}

export interface RankingPremioFaixa {
  ranking?: RankingTipo;
  de: number;
  ate: number;
  defId: string;
  nome: string;
  qtd: number;
}

export interface RankingTabela {
  tipo: RankingTipo;
  periodo: RankingPeriodo;
  ordem: 'kills' | 'kd';
  atualizadoEm: string;
  temporada: RankingTemporada;
  premios: readonly RankingPremioFaixa[];
  linhas: readonly RankingLinha[];
  voce: RankingLinha | null;
  guilda: string | null;
  regras: { kdMin: number; guildaKdMin: number; minimo: number; eloInicial: number };
}

/** O motivo como o jogador lê. Rede e aparelho chegam como "verif": nunca se conta que duas contas dividem a rede. */
export type RankingMotivo = '' | 'conta' | 'aliado' | 'iniciante' | 'protecao' | 'par' | 'verif' | 'equipe' | 'anulada' | 'erro';

export interface RankingEvento {
  id: number;
  tipo: 'kill' | 'morte';
  outro: string;
  tag: string;
  noto: number;
  mapa: string;
  contexto: 'mundo' | 'guerra';
  quando: string;
  resultado: 'ok' | 'nao' | 'verif';
  motivo: RankingMotivo;
  voltaAs: string | null;
}

export interface RankingMinhas {
  temporada: RankingTemporada;
  killsOk: number;
  killsNao: number;
  mortesOk: number;
  mortesNao: number;
  protecaoMin: number;
  eventos: readonly RankingEvento[];
}

export interface RankingRegras {
  ativo: boolean;
  versao: string;
  temporada: RankingTemporada;
  protecaoMin: number;
  parTeto: number;
  iniciantesH: number;
  kdMinKills: number;
  temporadaDias: number;
  guildaCarenciaH: number;
  guildaKdMin: number;
  eloInicial: number;
  eloK: number;
  ubMinimo: number;
  ubParDia: number;
  ubAbandonoS: number;
  dueloMinimo: number;
  dueloParDia: number;
  dueloAbandonoS: number;
  premios: readonly RankingPremioFaixa[];
}

/* ───────── Votar no TopIdle (docs/techspec/techspec-topidle-votos.md) ───────── */

export interface TopIdleEstado {
  /** Desligado no console (ou sem o id do jogo): o botão não aparece. */
  readonly ligado: boolean;
  /** O link do voto, já com o código da conta. Nulo quando desligado. */
  readonly url: string | null;
  readonly podeVotar: boolean;
  /** ms Unix: quando o botão acende de novo. Nulo quando já pode votar. */
  readonly proximoVotoEm: number | null;
  readonly ultimoVotoEm: number | null;
}

/* ───────── Correio (docs/techspec/techspec-correio.md) ───────── */

export type CorreioOrigem = 'equipe' | 'founder' | 'evento' | 'chefe' | 'pentagrama' | 'altar' | 'ranking' | 'duelo' | 'boas-vindas' | 'topidle';
export type CorreioTipoAnexo = 'item' | 'ouro' | 'prata' | 'vip' | 'traje' | 'selo';
export type CorreioDestino = 'mochila' | 'cofre' | 'conta' | 'guarda';
export type CorreioEstadoAnexo = 'pendente' | 'recebido' | 'cancelado';

export interface CorreioAnexo {
  readonly id: string;
  readonly tipo: CorreioTipoAnexo;
  /** O item, a peça do guarda-roupa ou o pack do selo; nulo no VIP. */
  readonly def: string | null;
  readonly qtd: number;
  readonly destino: CorreioDestino;
  readonly estado: CorreioEstadoAnexo;
  readonly dur: number | null;
  readonly qualidade: number;
  readonly feitoPor: string | null;
}

export interface CorreioCarta {
  readonly id: string;
  readonly origem: CorreioOrigem;
  readonly remetente: string;
  readonly titulo: string;
  readonly texto: string;
  /** ms Unix. */
  readonly em: number;
  readonly lida: boolean;
  /** Carta da conta: aparece em todos os personagens; o primeiro que receber leva. */
  readonly daConta: boolean;
  readonly anexos: readonly CorreioAnexo[];
}

export interface Correio {
  readonly cartas: readonly CorreioCarta[];
  readonly novas: number;
  readonly pendentes: number;
}

export interface CorreioResumo {
  readonly novas: number;
  readonly pendentes: number;
}

export interface CorreioRecebido {
  readonly carta: CorreioCarta;
  readonly recebidos: number;
  /** Os anexos que esperam, com o motivo (mochila, vip, cofre, item). */
  readonly naoCouberam: readonly { readonly anexo: string; readonly motivo: string }[];
}

/* ───────── Wiki (docs/techspec-wiki.md): GET /api/wiki/v1, público, só leitura ───────── */

export interface WikiArma { dado: string; velocidade: number; alcance: number; skill: string; municao: string | null }
export interface WikiBonus { velAtaqueBp: number; velMagiaBp: number; str: number; dex: number; int: number }
export interface WikiItem {
  id: string;
  /** Nome do banco, em português: só reserva. O site traduz por `item.{id}`. */
  nome: string;
  grupo: string;
  peso: number;
  empilha: boolean;
  maos: number;
  forca: number;
  durabilidade: number;
  peca: string;
  abencoado: boolean;
  vipPrata: boolean;
  arma: WikiArma | null;
  ar: number | null;
  cura: number | null;
  curaVeneno: boolean;
  bonus: WikiBonus | null;
  /** O material do item; a cor do ícone sai do id. */
  material: string | null;
  /** A base da variante (`katana` em `katana@valorite`); null na base. */
  base: string | null;
}
export interface WikiRegras { esperaPocaoS: number; peladoBp: number; studdedBp: number; boneBp: number; bonusMaximo: number; vidaBase: number }
export interface WikiClasse { id: string; str: number; dex: number; int: number; skills: { id: string; valor: number }[]; kit: string[] }
export interface WikiMagia {
  id: string;
  circulo: number;
  mana: number;
  skillMin: number;
  skillFull: number;
  /** Palavras de poder: não se traduzem. */
  palavras: string;
  danoMin: number;
  danoMax: number;
  alcance: number;
  atrasoMs: number;
  tipo: 'dano' | 'veneno' | 'curaVeneno' | 'cura' | 'viagem' | 'controle' | 'buff' | 'debuff' | 'ressurreicao' | 'outro';
}
export interface WikiSaque { item: string; qtdMin: number; qtdMax: number; chance: number }
export interface WikiMonstro {
  id: string;
  nome: string;
  aparencia: string;
  vida: number;
  ar: number;
  resistMagia: number;
  imuneVeneno: boolean;
  dano: string;
  golpeS: number;
  temperamento: 'hostil' | 'neutro' | 'passivo';
  prataMin: number;
  prataMax: number;
  saque: WikiSaque[];
  /** A paralisia pega neste monstro. */
  paralisa: boolean;
  /** Alcance do golpe, em casas (1 = corpo a corpo). */
  alcance: number;
}
/** Quantos deste monstro o mapa tem; vem do mais fraco para o mais forte. */
export interface WikiCacadaMob { id: string; qtd: number }
export interface WikiCacada { id: string; mapa: string; nivel: number; risco: string; skill: string; skillValor: number; mobs: WikiCacadaMob[] }
export interface WikiNpc { id: string; nome: string; titulo: string; servico: 'banco' | 'corretor' | 'guarda' | null; moeda: string | null; loja: { item: string; preco: number }[] }
export interface WikiCidade { id: string; nome: string; npcs: WikiNpc[] }
export interface WikiReceita { item: string; insumos: { item: string; qtd: number }[]; ferramenta: string | null; skillMin: number; skillMax: number | null; rende: number }
export interface WikiMetal { id: string; lingote: string; minerio: string; ferraria: number }
export interface WikiDados {
  mundo: number;
  mundoNumero: string;
  geradoEm: string;
  itens: WikiItem[];
  regras: WikiRegras;
  classes: WikiClasse[];
  skills: { id: string; grupo: string }[];
  magias: WikiMagia[];
  monstros: WikiMonstro[];
  cacadas: WikiCacada[];
  cidades: WikiCidade[];
  oficios: { ferraria: WikiReceita[]; metais: WikiMetal[]; alfaiataria: WikiReceita[]; arcos: WikiReceita[] };
  /* Wiki v2 (docs/techspec/techspec-wiki-v2.md §01) */
  materiais: WikiMaterial[];
  coleta: WikiColeta;
  craft: WikiCraft;
  traje: WikiTraje;
  loja: WikiLoja;
  pvp: WikiPvp;
  vip: WikiVip;
  /** Sistema › Eventos (Renato, 24/10): o documento de eventos publicado. */
  eventos: WikiEventos;
}
export type WikiFamilia = 'metal' | 'madeira' | 'couro' | 'osso';
export interface WikiMaterial {
  id: string; familia: WikiFamilia; nome: string; padrao: boolean; cor: string; insumo: string;
  skillColher: number; skillTrabalhar: number;
  /** Quanto a peça craftada neste material é mais forte, em %. */
  forca: number;
}
export interface WikiColetaLinha { material: string; item: string; skillMin: number; acertoDe: number; acertoAte: number; peso: number; ativa: boolean }
export interface WikiColetaRecurso {
  id: 'madeira' | 'minerio' | string; mapa: string; skill: string; familia: WikiFamilia; vagas: number;
  ferramentas: { item: string; bonus: number }[];
  regras: { tentativaSeg: number; porAcerto: number; vipColeta: number; vipGanho: number };
  linhas: WikiColetaLinha[];
}
export interface WikiColeta { recursos: WikiColetaRecurso[]; horasCheio: number }
export interface WikiCraftReceita { item: string; oficio: 'ferraria' | 'arcos' | string; qtd: number; pericia: number; bonus: number; excepcional: number }
export interface WikiCraft { receitas: WikiCraftReceita[]; chanceBase: number; chancePorPonto: number; chanceTeto: number; chanceMestre: number }
export interface WikiTrajeCor { id: string; nome: string; hue: number; prime: boolean; raridade: string | null; livre: boolean }
export interface WikiTraje {
  slots: { id: string; nome: string }[];
  pecas: { id: string; nome: string; slot: string; soMulher: boolean }[];
  cores: WikiTrajeCor[];
}
/** Texto da vitrine nos três idiomas; en e es podem faltar (cai no pt). */
export interface WikiFrase { pt: string; en: string | null; es: string | null }
export interface WikiLojaOferta { sku: string; nome: WikiFrase; descricao: WikiFrase; preco: number; tipo: string; item: string | null; dias: number | null; cor: string | null }
export interface WikiLoja { abas: { id: string; nome: WikiFrase; intro: WikiFrase; ofertas: WikiLojaOferta[] }[] }
export interface WikiPvp {
  dueloAtivo: boolean; conviteAlcance: number; conviteExpiraS: number; apostaItensMax: number; saqueTempoS: number;
  contagemPrimeiraS: number; contagemEntreS: number; rodadaDuracaoMaxS: number; rodadasMax: number; desconexaoEsperaMin: number;
  protecaoMin: number; parTeto: number; vitimaHoras: number; temporadaDias: number; eloInicial: number;
}
export interface WikiVip { ativo: boolean; pocaoAtiva: boolean; pocoes: { item: string; preco: number }[] }
/** Hora de Brasília; dias 1 = segunda … 7 = domingo. */
export interface WikiEventoHorario { tipo: 'ultimate-battle' | 'chefe-mundo' | 'pentagrama' | string; hora: string; dias: number[] }
export interface WikiDropItem { item: string; qtdMin: number; qtdMax: number; peso: number }
export interface WikiDropRaridade { id: string; chance: number; itens: WikiDropItem[] }
export interface WikiDropTabela { id: string; raridades: WikiDropRaridade[] }
export interface WikiChefeCor { id: string; nome: string; matiz: number; aparencia: string; mapa: string; montarias: string[] }
export interface WikiAltarOferta { item: string; nome: string; qtd: number; preco: number; cor: string | null; limitePeriodo: string; limiteQtd: number }
export interface WikiChefe {
  ativo: boolean; vidaBase: number; vidaPorJogador: number; duracaoS: number; avisoS: number; chuvaS: number; chuvaPct: number;
  invocaQtd: number; invocaMob: string; furiaPct: number; danoMinimoPct: number; prataMin: number; prataMax: number;
  fragmentos: number; fragmentosGolpeFinal: number; fragmentosTop3: number; capaBonus: number; capaRepetidaFragmentos: number;
  mapas: { mapa: string; tabela: string }[]; cores: WikiChefeCor[]; altar: WikiAltarOferta[]; bau: { raridade: string; chance: number }[];
}
export interface WikiPentaCor { id: string; nome: string; orbe: string; fragmento: string; guardiao: string; aparencia: string; matiz: number }
export interface WikiPentagrama {
  ativo: boolean; duracaoS: number; guardiaoAosS: number; ondaS: number; danoMinimo: number; fragmentosParticipar: number;
  fragmentosGuardiao: number; fragmentosTop3: number; fragmentosPorOrbe: number; prataPorOrbe: number; prataRitual: number;
  robeBonus: number; robe: string; cores: WikiPentaCor[];
}
export interface WikiEventos {
  agenda: WikiEventoHorario[];
  ub: { inscricao: number; moeda: string; porSala: number; minimo: number; partidaS: number; daemonS: number; podio: number[]; queimaPct: number; tabelaParticipacao: string; tabelaVitoria: string };
  chefe: WikiChefe;
  pentagrama: WikiPentagrama;
  tabelas: WikiDropTabela[];
}

/** Inspecionar (Renato, 16/10): o que se vê de outro jogador. Sem skills, atributos nem mochila. */
export interface Inspecao {
  readonly nome: string;
  readonly profissao: string;
  readonly notoriedade: string;
  readonly guildaTag: string;
  readonly guildaNome: string;
  readonly vip: boolean;
  readonly morto: boolean;
  readonly ar: number;
  readonly selo?: number;
  readonly conjunto: { readonly familia: string; readonly pecas: number; readonly pecasExigidas: number; readonly completo: boolean };
  readonly itens: readonly { readonly slot: string; readonly defId: string; readonly nome: string }[];
}

/* ───────── Parceiros (docs/techspec/techspec-parceiros.md) ───────── */

export interface ParceiroEu {
  readonly parceiro: boolean;
  readonly nome: string | null;
  readonly codigo: string | null;
  readonly estado: 'convidado' | 'ativo' | 'pausado' | null;
}

/** A sessão do site dos parceiros: a conta, o CSRF dela e quem é o parceiro. */
export interface ParceiroSessao {
  readonly account: { readonly id: string; readonly displayName: string };
  readonly csrfToken: string;
  readonly parceiro: ParceiroEu;
  /** Ainda com a senha provisória do e-mail: o painel só abre depois de trocar. */
  readonly trocarSenha: boolean;
}

/** Entrar ou trocar a senha: `pronto` (com a sessão) ou `codigo` (2FA da equipe). */
export interface ParceiroEntrada {
  readonly etapa: 'pronto' | 'codigo';
  readonly sessao?: ParceiroSessao | null;
  readonly desafio?: { readonly etapa: string; readonly desafio: string } | null;
}

/** Valores em centavos. `receita`, `comissao` e `emAnalise` vêm nulos quando o parceiro não vê valores. */
export interface ParceiroNumeros {
  readonly cliques: number;
  readonly inscricoes: number;
  readonly contas: number;
  readonly personagem: number;
  readonly jogando: number;
  readonly compraram: number;
  readonly comprasOuro: number;
  readonly compradoresOuro: number;
  readonly founder: number;
  readonly founderPorTier: Readonly<Record<string, number>>;
  readonly receita: number | null;
  readonly comissao: number | null;
  readonly emAnalise: number | null;
}

export interface ParceiroPainel {
  readonly nome: string;
  readonly codigo: string;
  readonly estado: 'convidado' | 'ativo' | 'pausado';
  readonly desde: string;
  readonly mostrar: 'comissao' | 'tudo' | 'contagens';
  readonly comissaoPct: number;
  readonly janelaDias: number;
  readonly validadeDias: number;
  readonly diasJogo: number;
  readonly linksPeloParceiro: boolean;
  readonly dias: number;
  readonly numeros: ParceiroNumeros;
  readonly serie: readonly { dia: string; cliques: number; contas: number; jogando: number }[];
  readonly canais: readonly { canal: string | null; cliques: number; contas: number; jogando: number; compras: number; receita: number | null }[];
  readonly feed: readonly { tipo: 'conta' | 'ouro' | 'founder'; nome: string | null; canal: string | null; tier: string | null; em: string }[];
  readonly meses: readonly {
    mes: string; receita: number | null; comissao: number; emAnalise: number; estado: 'aberto' | 'a_pagar' | 'pago'; pagoEm: string | null;
    pagamentoId: string | null; temComprovante: boolean;
  }[] | null;
  readonly aReceber: number | null;
  readonly jaPago: number | null;
}

export interface ParceiroLink {
  readonly id: string;
  readonly canal: string;
  readonly slug: string;
  readonly cliques: number;
  readonly criadoEm: string;
}

export interface ParceiroLinks {
  readonly codigo: string;
  readonly podeCriar: boolean;
  readonly maximo: number;
  readonly links: readonly ParceiroLink[];
}

/* ───────── Indicações (docs/techspec/techspec-indicacoes.md) ───────── */

/** 1 conta criada · 2 personagem criado · 3 jogando. */
export type EstadoIndicado = 1 | 2 | 3;

export interface Indicado {
  readonly nome: string | null;
  readonly estado: EstadoIndicado;
  /** ISO. */
  readonly em: string;
}

export interface Indicacao {
  /** Desligado no console: a linha some do Mais. */
  readonly ativo: boolean;
  /** Nulo enquanto a conta não tem personagem. */
  readonly codigo: string | null;
  /** ISO: até quando a troca de código está travada. Nulo = pode trocar. */
  readonly podeTrocarEm: string | null;
  readonly visitas: number;
  readonly contas: number;
  readonly jogando: number;
  readonly indicados: readonly Indicado[];
  readonly diasJogo: number;
  readonly validade: number;
  readonly marcos: readonly number[];
}

export interface OrigemVisita {
  readonly codigo: string | null;
  readonly utmSource: string | null;
  readonly utmMedium: string | null;
  readonly utmCampaign: string | null;
  readonly referer: string | null;
}
